import type { Texture } from 'pixi.js';
import type { FriendInfo } from '@goodtime/shared';
import { BoardDialog } from '../board/BoardDialog';
import { BrassButton, layoutButtonRow } from '../board/BrassButton';
import { FriendJoinContent, type Greeting } from './FriendJoinContent';
import type { ArtLookup } from './friendPieces';

/** What the player chose when a friend joined. */
export type FriendJoinChoice = 'play' | 'later';

const BUTTON_H = 48;

/**
 * A friend has just joined (their lamp lit): they say hello on a board over
 * the slot, and their game takes its lamp on the run. One button, LOVELY;
 * or, while their first game is free, Later on the left and PLAY FREE on
 * the right. The same card says when a star lights a Wheel Run lamp (a new
 * game), and carries one-off notices.
 */
export class FriendJoinDialog extends BoardDialog {
  private resolve: ((choice: FriendJoinChoice) => void) | null = null;
  private inner: FriendJoinContent | null = null;

  constructor(private readonly art: ArtLookup, floor: Texture | null) {
    super({ title: '', floor });
    this.label = 'friend-join';
  }

  /** Introduce `friend` (or, with `title`, offer their game again);
   *  resolves with what the player tapped. */
  async ask(friend: FriendInfo, freePlay: boolean, title = `${friend.name.toUpperCase()} JOINS YOU`): Promise<FriendJoinChoice> {
    return this.greet(friend, freePlay, title);
  }

  /** Show `greeting` under `title`; resolves with what the player tapped. */
  async greet(greeting: Greeting | FriendInfo, freePlay: boolean, title: string): Promise<FriendJoinChoice> {
    this.setTitle(title);
    this.content.removeChildren().forEach((c) => c.destroy({ children: true }));
    const inner = new FriendJoinContent(greeting, this.art, freePlay);
    this.inner = inner;
    const done = new Promise<FriendJoinChoice>((resolve) => {
      this.resolve = resolve;
    });
    const choose = (choice: FriendJoinChoice) => () => {
      const r = this.resolve;
      this.resolve = null;
      r?.(choice);
    };
    const action = new BrassButton({ label: freePlay ? 'PLAY FREE' : 'LOVELY', kind: 'gold', onTap: choose(freePlay ? 'play' : 'later') });
    const cancel = freePlay ? new BrassButton({ label: 'Later', kind: 'wood', onTap: choose('later') }) : null;
    this.content.addChild(inner, ...(cancel ? [cancel] : []), action);
    const width = this.contentWidth;
    const height = inner.layout(width);
    layoutButtonRow(cancel, action, 0, height + 16, width);
    this.setContentHeight(height + 16 + BUTTON_H);
    await this.open();
    inner.enter();
    const choice = await done;
    await this.close();
    return choice;
  }

  override layout(viewWidth: number, viewHeight: number): void {
    super.layout(viewWidth, viewHeight);
    // The content was laid out for the width it had when it opened; a resize
    // (a phone turned) lays it out again.
    if (this.inner && this.visible) {
      const width = this.contentWidth;
      const height = this.inner.layout(width);
      const buttons = this.content.children.filter((c): c is BrassButton => c instanceof BrassButton);
      const action = buttons[buttons.length - 1];
      if (action) layoutButtonRow(buttons.length > 1 ? buttons[0]! : null, action, 0, height + 16, width);
    }
  }
}
